import { NavLink } from 'react-router-dom';
import {
  FiHome,
  FiNavigation,
  FiUsers,
  FiHeart,
  FiUser,
  FiClock,
  FiMenu,
  FiChevronLeft,
  FiChevronRight,
  FiLogOut,
  FiUserPlus,
} from 'react-icons/fi';
import { useAuthStore, useUserStore, useUIStore } from '../../stores';
import { useLogout } from '../../hooks/auth-operations/useLogout';

// Navigation items for riders
const riderNavItems = [
  { to: '/riderhome', label: 'Home', icon: FiHome },
  { to: '/findride', label: 'Find Ride', icon: FiNavigation },
  { to: '/availabledrivers', label: 'All Drivers', icon: FiUsers },
  { to: '/favoritedrivers', label: 'Favorites', icon: FiHeart, requiresAuth: true },
  { to: '/riderprofile', label: 'Profile', icon: FiUser, requiresAuth: true },
  { to: '/riderhistory', label: 'History', icon: FiClock, requiresAuth: true },
];

// Navigation items for drivers
const driverNavItems = [
  { to: '/driverhome', label: 'Home', icon: FiHome },
  { to: '/inviterider', label: 'Invite Rider', icon: FiUserPlus },
  { to: '/driverprofile', label: 'Profile', icon: FiUser },
  { to: '/driverhistory', label: 'History', icon: FiClock },
];

// Navigation items for non-authenticated users
// Guest rides are disabled for the invite-only alpha, so the public nav
// doesn't offer Find Ride (it would just bounce to /unauthorized) — the
// Home CTA opens login instead.
const publicNavItems = [
  { to: '/', label: 'Home', icon: FiHome },
  { to: '/availabledrivers', label: 'All Drivers', icon: FiUsers },
];

export default function Sidebar() {
  const { isAuth } = useAuthStore();
  const { userType } = useUserStore();
  const { sidebarOpen, toggleSidebar, sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const logout = useLogout();

  // Determine which nav items to show based on auth state and user type
  let navItems = publicNavItems;
  if (isAuth && userType === 'driver') {
    navItems = driverNavItems;
  } else if (isAuth && userType === 'rider') {
    navItems = riderNavItems.filter((item) => !item.requiresAuth || isAuth);
  }

  // Determine the theme colors based on user type
  const isDriver = isAuth && userType === 'driver';
  const bgColor = isDriver ? 'bg-main-blue-dark' : 'bg-main-green-dark';
  const borderColor = isDriver ? 'border-main-blue-light' : 'border-main-green-light';
  const hoverBg = isDriver ? 'hover:bg-main-blue-light' : 'hover:bg-main-green-light';
  const activeBg = isDriver ? 'bg-main-blue-light' : 'bg-main-green-light';

  const handleLogout = async () => {
    try {
      await logout();
      if (sidebarOpen) toggleSidebar();
    } catch (error) {
      console.error('Handle logout error:', error);
    }
  };

  return (
    <>
      {/* Mobile toggle button - hidden when sidebar is open */}
      {!sidebarOpen && (
        <button
          onClick={toggleSidebar}
          className={`fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg ${bgColor} text-white shadow-lg`}
        >
          <FiMenu size={24} />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 ${bgColor} text-white transform transition-all duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'} w-64`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div
            className={`flex items-center h-16 border-b ${borderColor} ${
              sidebarCollapsed ? 'lg:justify-center lg:px-2' : 'px-6'
            }`}
          >
            <span className={`text-xl font-bold ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
              Arryde
            </span>
            <span className={`text-xl font-bold ${sidebarCollapsed ? 'hidden lg:block' : 'hidden'}`}>
              A
            </span>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-6 space-y-1 ${sidebarCollapsed ? 'lg:px-2' : 'px-4'}`}>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => {
                  // Close mobile sidebar on nav
                  if (sidebarOpen) toggleSidebar();
                }}
                title={sidebarCollapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex items-center py-3 rounded-lg transition-colors ${
                    sidebarCollapsed ? 'lg:justify-center lg:px-2' : 'px-4'
                  } ${
                    isActive
                      ? `${activeBg} text-white`
                      : `text-white/80 ${hoverBg} hover:text-white`
                  }`
                }
              >
                <item.icon className={sidebarCollapsed ? 'lg:mr-0' : 'mr-3'} size={20} />
                <span className={sidebarCollapsed ? 'lg:hidden' : ''}>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Logout button */}
          <div className={`py-2 ${sidebarCollapsed ? 'lg:px-2' : 'px-4'}`}>
            <button
              onClick={handleLogout}
              title={sidebarCollapsed ? (isAuth ? 'Logout' : 'Reset') : undefined}
              className={`flex items-center w-full py-3 rounded-lg transition-colors text-white/80 ${hoverBg} hover:text-white ${
                sidebarCollapsed ? 'lg:justify-center lg:px-2' : 'px-4'
              }`}
            >
              <FiLogOut className={sidebarCollapsed ? 'lg:mr-0' : 'mr-3'} size={20} />
              <span className={sidebarCollapsed ? 'lg:hidden' : ''}>
                {isAuth ? 'Logout' : 'Reset'}
              </span>
            </button>
          </div>

          {/* Collapse toggle button (desktop only) */}
          <div className={`hidden lg:flex justify-center py-2 border-t ${borderColor}`}>
            <button
              onClick={toggleSidebarCollapsed}
              className={`p-2 rounded-lg text-white/60 hover:text-white ${hoverBg} transition-colors`}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
            </button>
          </div>

          {/* Footer */}
          <div
            className={`py-4 border-t ${borderColor} ${
              sidebarCollapsed ? 'lg:px-2 lg:text-center' : 'px-6'
            }`}
          >
            <p className={`text-xs text-white/50 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
              {isDriver ? 'Driver Mode' : 'Rider Mode'}
            </p>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
}
